import type { ReactNode } from 'react';
import { Icon } from '@iconify/react';
import { Button } from '@/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/shadcn/dialog';

interface SportFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
}

export const sportDialogPrimaryClass =
  'h-11 flex-1 rounded-full bg-[#FC480D] font-bold text-white hover:bg-[#D83B08]';

export const sportDialogOutlineClass = 'h-11 flex-1 rounded-full';

export function SportFormDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
}: SportFormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-md gap-0 overflow-hidden rounded-3xl border-0 p-0 shadow-lg">
        <DialogHeader className="space-y-1 border-b border-gray-100 px-5 py-4 pr-12 text-left">
          <DialogTitle className="text-lg font-extrabold text-[#0E1726]">{title}</DialogTitle>
          {description ? (
            <DialogDescription className="text-sm text-gray-500">{description}</DialogDescription>
          ) : null}
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto px-5 py-5">{children}</div>
        {footer ? (
          <DialogFooter className="flex-row gap-2 border-t border-gray-100 px-5 py-4 sm:space-x-0">
            {footer}
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

interface PlayerRequirementDialogProps {
  open: boolean;
  title: string;
  message: string;
  onClose: () => void;
}

export function PlayerRequirementDialog({
  open,
  title,
  message,
  onClose,
}: PlayerRequirementDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-xs gap-3 rounded-3xl border-0 p-5 text-center shadow-lg">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#FFF1EC] text-[#FC480D]">
          <Icon icon="mdi:account-alert-outline" className="h-6 w-6" />
        </div>
        <DialogHeader className="space-y-1 text-center sm:text-center">
          <DialogTitle className="text-base font-extrabold text-[#0E1726]">{title}</DialogTitle>
          <DialogDescription className="text-sm text-gray-500">{message}</DialogDescription>
        </DialogHeader>
        <Button
          type="button"
          className="h-11 w-full rounded-full bg-[#FC480D] font-bold text-white hover:bg-[#D83B08]"
          onClick={onClose}
        >
          Mengerti
        </Button>
      </DialogContent>
    </Dialog>
  );
}

interface PlayerRequirementNoticeProps {
  title: string;
  message: string;
}

export function PlayerRequirementNotice({ title, message }: PlayerRequirementNoticeProps) {
  return (
    <div className="rounded-2xl bg-[#FFF8F5] px-4 py-6 text-center shadow-[inset_0_0_0_1px_#F6D2C4]">
      <p className="text-sm font-extrabold text-[#0E1726]">{title}</p>
      <p className="mt-1 text-sm text-gray-500">{message}</p>
    </div>
  );
}

interface SportDialogButtonProps {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
}

export function SportDialogPrimaryButton({
  children,
  onClick,
  disabled,
  type = 'button',
}: SportDialogButtonProps) {
  return (
    <Button type={type} className={sportDialogPrimaryClass} onClick={onClick} disabled={disabled}>
      {children}
    </Button>
  );
}

export function SportDialogOutlineButton({
  children,
  onClick,
  disabled,
  type = 'button',
}: SportDialogButtonProps) {
  return (
    <Button
      type={type}
      variant="outline"
      className={sportDialogOutlineClass}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </Button>
  );
}
